import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { Moment } from 'moment';
import { SharedModule } from '@Shared/shared.module';
import { DropdownOption } from '@Components/single-select-dropdown-list/single-select-dropdown-list.component';

export interface FilterBarRangeConfig {
    key: string;
    label: string;
}

export interface DateRangeValue {
    start: Moment | null;
    end: Moment | null;
}

export interface StatusOption {
    value: string;
    displayName: string;
}

export interface FilterBarState {
    accountIds: string[];
    searchTerm: string;
    ranges: Record<string, DateRangeValue>;
    statuses: string[];
}

export function emptyFilterBarState(): FilterBarState {
    return { accountIds: [], searchTerm: '', ranges: {}, statuses: [] };
}

@Component({
    selector: 'dashboard-filter-bar',
    imports: [SharedModule],
    providers: [
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
        { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } }
    ],
    templateUrl: './dashboard-filter-bar.component.html',
    styleUrl: './dashboard-filter-bar.component.scss'
})
export class DashboardFilterBarComponent {
    @Input() showAccount: boolean = true;
    @Input() accountOptions: DropdownOption[] = [];
    @Input() showSearch: boolean = true;
    @Input() showStatus: boolean = false;
    @Input() statusOptions: StatusOption[] = [];
    @Input() activeFilterCount: number = 0;

    private _dateRanges: FilterBarRangeConfig[] = [];
    @Input()
    set dateRanges(ranges: FilterBarRangeConfig[]) {
        this._dateRanges = ranges ?? [];
        this._dateRanges.forEach(r => {
            if (!this.rangeValues[r.key]) {
                this.rangeValues[r.key] = { start: null, end: null };
            }
        });
    }
    get dateRanges(): FilterBarRangeConfig[] {
        return this._dateRanges;
    }

    @Output() filtersChange = new EventEmitter<FilterBarState>();
    @Output() cleared = new EventEmitter<void>();

    accountFilter: string[] = [];
    searchTerm: string = '';
    rangeValues: Record<string, DateRangeValue> = {};
    statusFilter: string[] = [];

    get accountFilterLabel(): string {
        return this.multiSelectLabel(this.accountFilter, this.accountOptions, 'Account', 'Accounts');
    }

    get statusFilterLabel(): string {
        return this.multiSelectLabel(this.statusFilter, this.statusOptions, 'Status', 'Statuses');
    }

    private multiSelectLabel(selected: string[], options: { value: string; displayName: string }[], emptyLabel: string, pluralWord: string): string {
        if (selected.length === 0) return emptyLabel;
        if (selected.length === 1) {
            return options.find(o => o.value === selected[0])?.displayName ?? `1 ${emptyLabel}`;
        }
        return `${selected.length} ${pluralWord}`;
    }

    onAccountSelectionChange(accountIds: string[]): void {
        this.accountFilter = accountIds ?? [];
        this.emit();
    }

    onStatusSelectionChange(statuses: string[]): void {
        this.statusFilter = statuses ?? [];
        this.emit();
    }

    onRangeChange(): void {
        this.emit();
    }

    onSearchChange(value: string): void {
        this.searchTerm = value ?? '';
        this.emit();
    }

    clear(): void {
        this.accountFilter = [];
        this.searchTerm = '';
        this.statusFilter = [];
        Object.keys(this.rangeValues).forEach(key => this.rangeValues[key] = { start: null, end: null });
        this.cleared.emit();
    }

    private emit(): void {
        const ranges: Record<string, DateRangeValue> = {};
        Object.keys(this.rangeValues).forEach(key => ranges[key] = { ...this.rangeValues[key] });
        this.filtersChange.emit({
            accountIds: [...this.accountFilter],
            searchTerm: this.searchTerm,
            ranges,
            statuses: [...this.statusFilter]
        });
    }
}
